import React, { useState, useRef } from 'react';
import { useUploadImageMutation } from '@Pages/AutomationBuilderV0/apiSlice';
import { displayToast } from '@Utils/toast';
import { AnalyticsParamBuilder } from '@Utils/common';
import posthog from 'posthog-js';

interface ImageUploadComponentProps {
  value: string;
  onChange: (imageUrl: string) => void;
  onBlur?: () => void;
  automationId?: number;
  cardIndex?: number;
  component?: string;
}

const ImageUploadComponent: React.FC<ImageUploadComponentProps> = ({
  value,
  onChange,
  onBlur,
  automationId,
  cardIndex,
  component = 'card_editor'
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadImage] = useUploadImageMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      displayToast('error', 'Please select a valid image file');
      return;
    }

    // Validate file size (max 1.5MB)
    const maxSize = 1.5 * 1024 * 1024;
    if (file.size > maxSize) {
      const currentSize = formatFileSize(file.size);
      displayToast('error', `Image size (${currentSize}) exceeds the maximum limit of 1.5MB`);
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);
      if (automationId) {
        formData.append('automation_id', automationId.toString());
      }

      posthog.capture('ui_action', AnalyticsParamBuilder({
        kingdom: component,
        phylum: 'image_upload',
        classValue: 'start_upload',
        genus: `card_${cardIndex}`,
        int_value: automationId
      }));

      const response = await uploadImage(formData).unwrap();
      
      if (response?.success && response?.image_url) {
        onChange(response.image_url);
        displayToast('success', 'Image uploaded successfully!');
        
        posthog.capture('api_response', AnalyticsParamBuilder({
          kingdom: component,
          phylum: 'success',
          classValue: 'image_upload',
          genus: `card_${cardIndex}`,
          str_value: response,
          int_value: automationId
        }));
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || 'Failed to upload image';
      displayToast('error', errorMessage);
      
      posthog.capture('api_response', AnalyticsParamBuilder({
        kingdom: component,
        phylum: 'error',
        classValue: 'image_upload',
        genus: `card_${cardIndex}`,
        str_value: errorMessage,
        int_value: automationId
      }));
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleChooseImage = () => {
    posthog.capture('ui_action', AnalyticsParamBuilder({
      kingdom: component,
      phylum: 'image_upload',
      classValue: 'choose_image_click',
      genus: `card_${cardIndex}`,
      int_value: automationId
    }));
    fileInputRef.current?.click();
  };

  const handleChangeImage = () => {
    posthog.capture('ui_action', AnalyticsParamBuilder({
      kingdom: component,
      phylum: 'image_upload',
      classValue: 'change_image_click',
      genus: `card_${cardIndex}`,
      int_value: automationId
    }));
    fileInputRef.current?.click();
  };

  const handleDeleteImage = () => {
    posthog.capture('ui_action', AnalyticsParamBuilder({
      kingdom: component,
      phylum: 'image_upload',
      classValue: 'delete_image_click',
      genus: `card_${cardIndex}`,
      int_value: automationId
    }));
    onChange('');
    if (onBlur) onBlur();
  };

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      
      {/* Image Preview */}
      <div className="mt-2 mx-auto w-[250px] h-48 rounded-lg overflow-hidden flex items-center justify-center bg-gray-100 border relative">
        {value ? (
          <>
            <img
              src={value}
              alt="Card preview"
              className="w-full h-full object-cover"
            />
            {/* Change Button Overlay */}
            <button
              onClick={handleChangeImage}
              disabled={isUploading}
              className="absolute bottom-3 left-3 bg-white bg-opacity-90 hover:bg-opacity-100 px-3 py-1.5 rounded-full flex items-center gap-2 text-sm font-medium text-gray-700 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Change
            </button>
            {/* Delete Button */}
            <button
              onClick={handleDeleteImage}
              disabled={isUploading}
              className="absolute top-3 right-3 bg-white bg-opacity-90 hover:bg-opacity-100 p-2 rounded-full text-gray-600 hover:text-red-600 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </>
        ) : (
          <div className="text-center">
            {isUploading ? (
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                <p className="text-sm text-gray-500">Uploading...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-500">Select an Image</p>
                <p className="text-xs text-gray-400 mt-1">Max size: 1.5MB</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {!value && (
        <div className="mt-4">
          <button
            onClick={handleChooseImage}
            disabled={isUploading}
            className="w-full px-4 py-2 bg-[#3535d5] text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isUploading ? 'Uploading...' : 'Choose Image'}
          </button>
        </div>
      )}

      {/* Manual URL Input (fallback) */}
      {/* <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Or enter image URL manually</label>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder="Enter image URL"
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div> */}
    </div>
  );
};

export default ImageUploadComponent;